import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion    } from 'framer-motion';
import { RiMapPinLine, RiHeartLine, RiHeartFill, RiChat3Line, RiMap2Line, RiLoader4Line, RiArrowLeftLine, RiStarFill, RiSendPlaneLine, RiExternalLinkLine } from 'react-icons/ri';
import Footer from '../components/Footer';
import { API_BASE_URL } from '../config';

const PlaceDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [place, setPlace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isLiked, setIsLiked] = useState(false);
    const [feedback, setFeedback] = useState({ name: '', comment: '', rating: 5 });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchPlaceDetails();
    }, [id]);

    const fetchPlaceDetails = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/travel/places/get_details.php?id=${id}`);
            const data = await response.json();
            if (response.ok) {
                setPlace(data);
            } else {
                navigate('/travel');
            }
        } catch (error) {
            console.error('Error fetching place details:', error);
            navigate('/travel');
        } finally {
            setLoading(false);
        }
    };

    const handleLike = async () => {
        if (isLiked) return;
        try {
            const response = await fetch(`${API_BASE_URL}/travel/places/like.php?id=${id}`, { method: 'POST' });
            if (response.ok) {
                setIsLiked(true);
                setPlace(prev => ({ ...prev, likes_count: parseInt(prev.likes_count) + 1 }));
                // Store like status in localStorage to prevent multiple likes from same browser
                localStorage.setItem(`liked_place_${id}`, 'true');
            }
        } catch (error) {
            console.error('Error liking place:', error);
        }
    };

    const handleSubmitFeedback = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const response = await fetch(`${API_BASE_URL}/travel/places/feedback.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    place_id: id,
                    user_name: feedback.name,
                    comment: feedback.comment,
                    rating: feedback.rating
                })
            });

            if (response.ok) {
                alert("Thank you! Your feedback has been shared.");
                setFeedback({ name: '', comment: '', rating: 5 });
                fetchPlaceDetails(); // Refresh list
            } else {
                alert("Failed to submit feedback. Please try again.");
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
            alert("An error occurred. Please try again later.");
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <RiLoader4Line className="animate-spin text-emerald-500 text-4xl" />
            </div>
        );
    }

    if (!place) return null;

    return (
        <div className="bg-white min-h-screen pt-32">
            <div className="container mx-auto px-6 max-w-7xl pb-24">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-12">
                    <div className="max-w-3xl">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-slate-400 hover:text-emerald-600 transition-colors mb-6 group"
                        >
                            <RiArrowLeftLine className="group-hover:-translate-x-1 transition-transform" />
                            <span className="text-[10px] font-black uppercase tracking-widest">Back to Region</span>
                        </button>

                        <div className="flex items-center gap-3 mb-4">
                            <span className="bg-emerald-50 text-emerald-600 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border border-emerald-100">
                                {place.category_name}
                            </span>
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                {place.district_name} District
                            </span>
                        </div>

                        <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight font-display">
                            {place.name}
                        </h1>
                        <p className="text-base sm:text-lg text-slate-500 font-medium leading-relaxed font-sans">
                            {place.description}
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        <motion.button
                            whileTap={{ scale: 0.9 }}
                            onClick={handleLike}
                            className={`flex flex-col items-center gap-1 p-4 rounded-3xl border transition-all ${isLiked ? 'bg-rose-50 border-rose-100 text-rose-500' : 'bg-slate-50 border-slate-100 text-slate-400 hover:border-rose-200 hover:text-rose-400'}`}
                        >
                            {isLiked ? <RiHeartFill size={28} /> : <RiHeartLine size={28} />}
                            <span className="text-xs font-black">{place.likes_count}</span>
                        </motion.button>
                        <div className="flex flex-col items-center gap-1 p-4 rounded-3xl border border-slate-50 text-slate-400">
                            <RiChat3Line size={28} />
                            <span className="text-xs font-black">{place.feedback?.length || 0}</span>
                        </div>
                    </div>
                </div>

                {/* Main Visual */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative aspect-[21/9] rounded-[40px] overflow-hidden shadow-2xl mb-16"
                >
                    <img src={place.image_url} alt={place.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent" />
                </motion.div>

                <div className="grid lg:grid-cols-3 gap-16">
                    {/* Content Column */}
                    <div className="lg:col-span-2 space-y-12">
                        <section>
                            <h3 className="text-2xl font-bold text-slate-900 mb-6 font-display">About the Destination</h3>
                            <div className="prose prose-slate max-w-none">
                                <p className="text-base sm:text-lg text-slate-600 leading-relaxed whitespace-pre-wrap font-sans">
                                    {place.content}
                                </p>
                            </div>
                        </section>

                        {/* Maps Section */}
                        {place.map_location && (
                            <section>
                                <h3 className="flex items-center gap-3 text-2xl font-bold text-slate-900 mb-6 font-display">
                                    <RiMap2Line className="text-emerald-500" />
                                    Location Intelligence
                                </h3>
                                <div className="aspect-video rounded-[32px] overflow-hidden bg-slate-100 border border-slate-200">
                                    {place.map_location.includes('iframe') ? (
                                        <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: place.map_location }} />
                                    ) : (
                                        <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-center p-8">
                                            <RiMapPinLine size={48} className="text-emerald-600 animate-bounce" />
                                            <div>
                                                <p className="font-bold text-slate-900 mb-2">Google Maps Direction Ready</p>
                                                <a
                                                    href={place.map_location}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white font-bold text-xs uppercase tracking-widest hover:bg-emerald-600 transition-colors"
                                                >
                                                    Open Navigation <RiExternalLinkLine />
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}

                        {/* Feedback List */}
                        <section>
                            <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-8 flex items-center gap-3 font-display">
                                Community Feedback
                                <span className="text-sm font-black text-slate-300 bg-slate-50 px-3 py-1 rounded-lg font-sans">
                                    {place.feedback?.length || 0}
                                </span>
                            </h3>

                            <div className="space-y-6">
                                {place.feedback && place.feedback.length > 0 ? (
                                    place.feedback.map((item) => (
                                        <div key={item.id} className="p-8 rounded-[32px] bg-slate-50 border border-slate-100">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <p className="font-bold text-slate-900 font-display">{item.user_name}</p>
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest font-sans">
                                                        {new Date(item.created_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                                <div className="flex gap-0.5 text-amber-400">
                                                    {[...Array(5)].map((_, i) => (
                                                        <RiStarFill key={i} className={i < item.rating ? 'fill-current' : 'text-slate-200'} />
                                                    ))}
                                                </div>
                                            </div>
                                            <p className="text-slate-600 leading-relaxed font-medium capitalize">
                                                {item.comment}
                                            </p>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-12 p-8 border-2 border-dashed border-slate-100 rounded-[32px]">
                                        <p className="text-slate-400 font-medium italic">No feedback yet. Be the first to share your experience!</p>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>

                    {/* Sidebar / Form */}
                    <div className="space-y-8">
                        <section className="sticky top-40 bg-white p-8 rounded-[32px] border border-slate-100 shadow-xl shadow-slate-900/5">
                            <h4 className="text-xl font-bold text-slate-900 mb-2">Share Experience</h4>
                            <p className="text-sm text-slate-500 mb-8 font-medium">Your insights help others discover the best of {place.name}.</p>

                            <form onSubmit={handleSubmitFeedback} className="space-y-6">
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Your Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={feedback.name}
                                        onChange={(e) => setFeedback({ ...feedback, name: e.target.value })}
                                        className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20"
                                        placeholder="Full Name"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Rating</label>
                                    <div className="flex gap-2 p-2 bg-slate-50 rounded-2xl">
                                        {[1, 2, 3, 4, 5].map((num) => (
                                            <button
                                                key={num}
                                                type="button"
                                                onClick={() => setFeedback({ ...feedback, rating: num })}
                                                className={`flex-1 aspect-square rounded-xl flex items-center justify-center transition-all ${feedback.rating === num ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-300 hover:text-slate-400'}`}
                                            >
                                                <RiStarFill size={20} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2 block">Review Insight</label>
                                    <textarea
                                        required
                                        rows={4}
                                        value={feedback.comment}
                                        onChange={(e) => setFeedback({ ...feedback, comment: e.target.value })}
                                        className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 resize-none"
                                        placeholder="Tell us about your visit..."
                                    />
                                </div>
                                <button
                                    disabled={sending}
                                    className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:bg-emerald-600 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                                >
                                    {sending ? <RiLoader4Line className="animate-spin" /> : <RiSendPlaneLine />}
                                    Submit Insight
                                </button>
                            </form>
                        </section>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default PlaceDetails;
